import { useCallback, useEffect, useMemo } from 'react';
import { useRef } from 'react';
import { useSelector } from 'react-redux';
import useClientErrorHandler, { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import * as clientErrTypes from 'config/clientErrorTypes';
import useClientHistory, { RESULT } from 'client/hooks/useClientHistory';
import { retryAIJobApi } from 'api/client';
import { isTimeExpired } from 'utils/dateTime';
import { getAIJobsApi } from 'api/client';
import { AI_JOB_STATUS } from 'config/aiConfig';
import { setSessionStorage } from 'utils/setSessionStorage';

/**
 * AI Job 的 Hook
 * @param {Object} params - 參數物件
 * @param {number} [params.retryMaxLimitTime=1800000] - 最大重試時間限制，預設為 3 分鐘 (10 * 60 * 1000 毫秒)
 * @param {Function} params.setIsShowRetryDialog - 設定是否顯示重試對話框的函數
 * @returns {Object} 回傳包含處理函數的物件
 * @returns {Function} returns.onAIJobsCheck - 初檢查 AI Job 狀態的函數
 * @returns {Function} returns.onRedirect - 處理重新導向的函數
 * @returns {Function} returns.onClearTimer - 清除計時器的函數
 */
export default function useAIJobs({ retryMaxLimitTime = 3 * 60 * 1000, setIsShowRetryDialog }) {
    const history = useClientHistory();
    const errHandler = useClientErrorHandler();
    const { endDate, timezone } = useSelector(s => s.settingPromo);
    const { forceStayDurationSecond = 0 } = useSelector(s => s.settingStyle);
    const retryStartTime = useRef(null);
    const timer = useRef(null);
    const startTime = useRef(Date.now());

    // 組件卸載時清理 timer
    useEffect(() => {
        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
                timer.current = null;
            }
        };
    }, []);

    // 使用 useMemo 緩存計算結果，避免重複計算
    const minStayTime = useMemo(() => forceStayDurationSecond * 1000, [forceStayDurationSecond]);

    const checkPromoEnd = useCallback(() => {
        if (endDate && isTimeExpired(endDate, timezone, 1)) {
            // 活動已結束超過 1 小時，停止重試
            throw generateApiErrObj({ endDate, timezone }, clientErrTypes.AI_FAILED_WITH_PROMO_END);
        }
    }, [endDate, timezone]);

    const handleRedirect = useCallback(
        ({ redirectTarget }) => {
            const viewTime = Date.now() - startTime.current;

            if (viewTime < minStayTime) {
                clearTimeout(timer.current);
                timer.current = setTimeout(() => {
                    history.push(redirectTarget);
                }, minStayTime - viewTime);
                return;
            }

            history.push(redirectTarget);
        },
        [minStayTime, history]
    );

    // 計算隨機間隔時間
    const getRandomInterval = useCallback(() => {
        return Math.floor(Math.random() * 15000) + 10000;
    }, []);

    // 檢查是否超過最大重試時間
    const isRetryTimeExceeded = useCallback(
        (additionalTime = 0) => {
            if (!retryStartTime.current) return false;
            const currentTime = Date.now();
            const elapsedTime = currentTime - retryStartTime.current;
            return elapsedTime + additionalTime >= retryMaxLimitTime;
        },
        [retryMaxLimitTime]
    );

    /**
     * 檢查 AI Job 狀態
     * 如果有失敗的 Job 就顯示 dialog，不進行重試
     * @param {string} promoUUID - 促銷活動 UUID
     * @param {string} entryUUID - 參賽記錄 UUID
     * @param {boolean} isRetry - 是否重試
     */
    const handleAIJobsCheck = useCallback(
        async (promoUUID, entryUUID, isRetry) => {
            if (!promoUUID || !entryUUID) return;

            const check = async doRetry => {
                retryStartTime.current = Date.now();

                try {
                    clearTimeout(timer.current);
                    if (doRetry) {
                        // 檢查活動是否已結束
                        checkPromoEnd();
                    }

                    const aiJobData = await getAIJobData({
                        promoUUID,
                        entryUUID
                    });

                    const { isAllCompleted, failed = [] } = aiJobData;

                    if (isAllCompleted) {
                        setSessionStorage({
                            key: 'isTransitionPageFinished',
                            value: entryUUID,
                            isNoJSONStringify: true
                        });
                        // 所有 AI Job 已完成，導向結果頁面
                        handleRedirect({
                            redirectTarget: RESULT
                        });
                        return;
                    }

                    if (failed.length > 0) {
                        if (doRetry) {
                            await Promise.all(
                                failed.map(jobUUID =>
                                    retryAIJobApi({
                                        promoUUID,
                                        entryUUID,
                                        jobUUID
                                    })
                                )
                            );
                        } else {
                            // 有失敗的 Job，顯示重試對話框
                            setIsShowRetryDialog(true);
                            return;
                        }
                    }

                    // 沒有失敗但未全部完成，繼續循環檢查
                    const randomInterval = getRandomInterval();

                    // 檢查下次檢查是否會超過時間限制
                    if (isRetryTimeExceeded(randomInterval)) {
                        // 下次檢查會超過時間限制，停止重試
                        setIsShowRetryDialog(true);
                        return;
                    }

                    // 沒有失敗且未全部完成，定期檢查直到全部完成或有失敗
                    timer.current = setTimeout(() => {
                        // 檢查組件是否仍然掛載
                        if (timer.current) {
                            check();
                        }
                    }, randomInterval);
                } catch (error) {
                    if (error.message === clientErrTypes.AI_FAILED_WITH_PROMO_END) {
                        errHandler({ err: error });
                        return;
                    }
                    console.error('Init check AI Job error:', error);
                    setIsShowRetryDialog(true);
                }
            };
            check(isRetry);
        },
        [
            getRandomInterval,
            isRetryTimeExceeded,
            handleRedirect,
            setIsShowRetryDialog,
            errHandler,
            checkPromoEnd
        ]
    );

    const handleClearTimer = useCallback(() => {
        if (timer.current) {
            clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);

    return {
        onAIJobsCheck: handleAIJobsCheck,
        onRedirect: handleRedirect,
        onClearTimer: handleClearTimer
    };
}

/**
 * 取得 AI Job 資料並檢查狀態
 * @param {Object} params - 參數物件
 * @param {string} params.promoUUID - 活動 UUID
 * @param {string} params.entryUUID - 參加紀錄 UUID
 * @returns {Promise<Object>} AI 工作狀態資料
 * @returns {boolean} returns.isAllCompleted - 是否所有工作都已完成
 * @returns {Array<string>} returns.process - 處理中的工作 UUID 陣列
 * @returns {Array<string>} returns.completed - 已完成的工作 UUID 陣列
 * @returns {Array<string>} returns.failed - 失敗的工作 UUID 陣列
 */
export const getAIJobData = async ({ promoUUID, entryUUID }) => {
    const aiJobs = await getAIJobsApi({
        promoUUID,
        entryUUID
    });

    const result = Object.entries(aiJobs).reduce(
        (acc, [, value]) => {
            if (!value) return acc;

            value.forEach(item => {
                const { status, uuid } = item;

                if (status === AI_JOB_STATUS.COMPLETED) acc.completed.push(uuid);
                if (status === AI_JOB_STATUS.PROCESSING || status === AI_JOB_STATUS.PENDING) {
                    acc.process.push(uuid);
                }
                if (status === AI_JOB_STATUS.FAILED || status === AI_JOB_STATUS.TIME_OUT) {
                    acc.failed.push(uuid);
                }
            });

            return acc;
        },
        {
            process: [],
            completed: [],
            failed: []
        }
    );

    const isAllCompleted = result.process.length === 0 && result.failed.length === 0;

    return {
        isAllCompleted,
        ...result
    };
};
